import React from 'react';
import { IOffer, IOfferAction, ITableDemandSettings, OfferActionTypes } from '@models/winnerSelection/ITableDemand';
import OfferRate from '../OfferRate';
import OfferTechAnalysis from '../OfferTechAnalysis';
import OfferCustomForm from '../OfferCustomForm';
import OfferApproveToStageTwo from '../OfferApproveToStageTwo';
import OfferRevaluate from '../OfferRevaluate';
import OfferRatePKO from '../OfferRatePKO';
import OfferAttachmentsArchive from '../OfferAttachmentsArchive';
import { OfferModalKey } from '../hooks/useOfferModals';

interface IProps {
    tableKey: string;
    demandId: string;
    /** Текущий выбранный оффер (из useOfferModals.offerToEdit). */
    offer: IOffer | undefined;
    /** Контекст action — нужен только для customForm. */
    action: IOfferAction | undefined;
    /** Текущая открытая модалка ('rate' / 'ratePko' / …). */
    activeModal: OfferModalKey | undefined;
    /** Закрыть модалку (из useOfferModals.close). */
    onClose: () => void;
    /** Пере-загрузить данные таблицы после успешного сохранения. */
    onReload: () => void;
    /** Поля настроек, прокидываются в части модалок. */
    settings: ITableDemandSettings | undefined;
}

/**
 * Все 6 модалок Demand-таблицы в одном месте.
 *
 * Раньше — 6 блоков {offerToEdit && showXxxModal ? <OfferXxx ... /> : null} в
 * хвосте TableDemand. Теперь — один компонент, который выбирает нужную модалку
 * по activeModal, и одинаковая логика onClose/onSubmit для всех (close()
 * сбрасывает offer/action/activeModal сразу).
 */
export const OfferActionModals: React.FC<IProps> = ({
    tableKey,
    demandId,
    offer,
    action,
    activeModal,
    onClose,
    onReload,
    settings,
}) => {
    if (!offer || !activeModal) return null;

    const hasAction = (type: OfferActionTypes) => offer.actions.some((a) => a.type === OfferActionTypes[type]);
    const handleSubmit = () => {
        onReload();
        onClose();
    };

    switch (activeModal) {
        case 'rate':
            return (
                <OfferRate
                    tableKey={tableKey}
                    isEdit={hasAction(OfferActionTypes.RateOffer)}
                    value={offer}
                    demandId={demandId}
                    onSubmit={handleSubmit}
                    onClose={onClose}
                    offerUnAcceptableHint={settings?.offerUnAcceptableHint}
                    lowCostPurchase={settings?.lowCostPurchase}
                />
            );
        case 'techAnalysis':
            return (
                <OfferTechAnalysis
                    tableKey={tableKey}
                    isEdit={hasAction(OfferActionTypes.TechAnalysisOffer)}
                    value={offer}
                    demandId={demandId}
                    onSubmit={handleSubmit}
                    onClose={onClose}
                />
            );
        case 'revaluate':
            return (
                <OfferRevaluate
                    tableKey={tableKey}
                    isEdit={hasAction(OfferActionTypes.Revaluate)}
                    value={offer}
                    demandId={demandId}
                    onSubmit={handleSubmit}
                    onClose={onClose}
                />
            );
        case 'ratePko':
            return (
                <OfferRatePKO
                    tableKey={tableKey}
                    isEdit={hasAction(OfferActionTypes.RateOfferPKO)}
                    value={offer}
                    demandId={demandId}
                    onSubmit={handleSubmit}
                    onClose={onClose}
                    offerUnAcceptableHint={settings?.offerUnAcceptableHint}
                />
            );
        case 'customForm':
            if (!action) return null;
            return <OfferCustomForm tableKey={tableKey} value={offer} action={action} onClose={onClose} />;
        case 'approveToStageTwo':
            return (
                <OfferApproveToStageTwo
                    tableKey={tableKey}
                    isEdit={hasAction(OfferActionTypes.ApproveToStageTwo)}
                    value={offer}
                    demandId={demandId}
                    onSubmit={handleSubmit}
                    onClose={onClose}
                />
            );
        case 'attachmentsArchive':
            if (!action) return null;
            return (
                <OfferAttachmentsArchive tableKey={tableKey} offerKey={offer.key} action={action} onClose={onClose} />
            );
        default:
            return null;
    }
};
